import type { ReactNode, HTMLAttributes } from "react";
import { cn } from "@/lib/utils";

type SectionTone =
  | "light"
  | "cream"
  | "white"
  | "dark"
  | "navy-gradient"
  | "cream-gradient"
  | "agro-gradient"
  | "transparent";

type SectionSize = "default" | "sm" | "lg" | "hero" | "none";

type SectionProps = HTMLAttributes<HTMLElement> & {
  tone?: SectionTone;
  size?: SectionSize;
  innerClassName?: string;
  containerMaxWidth?: "5xl" | "6xl" | "7xl" | "full";
  withOverflowClip?: boolean;
  children: ReactNode;
};

const toneClass: Record<SectionTone, string> = {
  light: "bg-white",
  cream: "bg-[var(--sr-cream)]",
  white: "bg-white",
  dark: "bg-[var(--sr-navy-deep)]",
  "navy-gradient":
    "bg-gradient-to-br from-[#0a1628] via-[var(--sr-navy)] to-[var(--sr-navy-deep)]",
  "cream-gradient": "bg-gradient-to-b from-white to-[var(--sr-cream)]",
  "agro-gradient":
    "bg-gradient-to-br from-[#f0f7f0] via-white to-[var(--sr-cream)]",
  transparent: "",
};

const sizeClass: Record<SectionSize, string> = {
  default: "py-[var(--space-section)]",
  sm: "py-[var(--space-section-sm)]",
  lg: "py-[clamp(5rem,10vw,9rem)]",
  hero: "min-h-[100svh] flex items-center",
  none: "",
};

const containerMaxClass: Record<
  NonNullable<SectionProps["containerMaxWidth"]>,
  string
> = {
  "5xl": "max-w-5xl",
  "6xl": "max-w-6xl",
  "7xl": "max-w-7xl",
  full: "max-w-full",
};

/**
 * Wrapper de section padrão SR, tonalidade, padding e container unificados.
 * Uso:
 *   <Section id="veiculos" tone="navy-gradient" containerMaxWidth="7xl">
 *     <SectionHeader ... />
 *     ...
 *   </Section>
 */
export function Section({
  tone = "white",
  size = "default",
  innerClassName,
  containerMaxWidth = "7xl",
  withOverflowClip = false,
  className,
  children,
  ...rest
}: SectionProps) {
  return (
    <section
      className={cn(
        "relative w-full",
        toneClass[tone],
        sizeClass[size],
        withOverflowClip && "overflow-hidden",
        className
      )}
      {...rest}
    >
      <div
        className={cn(
          "relative z-10 w-full mx-auto px-4 sm:px-6",
          containerMaxClass[containerMaxWidth],
          innerClassName
        )}
      >
        {children}
      </div>
    </section>
  );
}
